﻿package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.GlobalManager
import org.example.project.service.KundenverbrauchService
import org.example.project.service.ZustandManager
import org.example.project.utils.All

@Composable
fun Kundensetup(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    val kundenLiveVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("company")
    )
    var kunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var draftKunden by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }

    fun deriveKundenZustand(kunde: All): Int = when {
        kunde.gesperrt == true -> ZustandManager.Kunde.GESPERRT
        kunde.zustand == ZustandManager.Kunde.STORNIERT -> ZustandManager.Kunde.STORNIERT
        kunde.zustand == ZustandManager.Kunde.INSTALLATION_ABGESCHLOSSEN -> ZustandManager.Kunde.INSTALLATION_ABGESCHLOSSEN
        else -> ZustandManager.Kunde.ANGELEGT
    }

    fun checkboxText(value: Boolean?): String = if (value == true) "\u2705" else "\u2b1c"
    fun nodeEingerichtet(kunde: All): Boolean = kunde.nodeEingerichtet

    suspend fun mergeKunden(): List<All> {
        val register = KundenSetupApi.loadCompanies()
            .sortedBy { it.firma.lowercase() }
        val lizByFirmenId = KundenSetupApi.loadAllDetails()
            .associateBy { it.firmenId.ifBlank { it.id } }

        return register.map { firma ->
            val firmenId = firma.firmenId.ifBlank { firma.id }
            val liz = lizByFirmenId[firmenId]
            val setupKey = liz?.firmenId.orEmpty().ifBlank { firmenId }
            val isInstalled = setupKey.isNotBlank() && KundenSetupApi.customerNodeExists(setupKey)
            val merged = firma.copy(
                id = firmenId,
                firmenId = firmenId,
                    lizenzkey = liz?.lizenzkey.orEmpty(),
                    nodePort = liz?.nodePort.orEmpty(),
                    rechnungsdatum = liz?.rechnungsdatum.orEmpty().ifBlank { "1" },
                gesperrt = firma.gesperrt,
                sperrgrund = firma.sperrgrund,
                aktiv = firma.zustand == ZustandManager.Kunde.INSTALLATION_ABGESCHLOSSEN,
                kunde = firma.kunde,
                zustand = firma.zustand,
                nodeEingerichtet = isInstalled && liz?.nodeEingerichtet == true
            )
            merged.copy(
                zustand = deriveKundenZustand(merged)
            )
        }
    }

    fun mapRow(kunde: All): List<String> = listOf(
        kunde.id,
        kunde.firmenId,
        kunde.firma,
        kunde.strasse,
        kunde.hausnummer,
        kunde.plz,
        kunde.ort,
        kunde.land,
        checkboxText(kunde.istnetzwerk),
        checkboxText(kunde.istkunde),
        ZustandManager.Kunde.textFor(kunde.zustand),
        kunde.lizenzkey,
        kunde.nodePort,
        kunde.rechnungsdatum.ifBlank { "1" },
        checkboxText(nodeEingerichtet(kunde)),
        checkboxText(kunde.gesperrt),
        kunde.sperrgrund.orEmpty()
    )

    fun buildKunde(id: String, cols: List<String>, existing: All?): All {
        val current = existing ?: All(id = id)
        val base = current.copy(
            id = id.ifBlank { current.id },
            firmenId = cols.getOrElse(1) { current.firmenId }.ifBlank { current.firmenId },
            firma = cols.getOrElse(2) { current.firma },
            strasse = cols.getOrElse(3) { current.strasse },
            hausnummer = cols.getOrElse(4) { current.hausnummer },
            plz = cols.getOrElse(5) { current.plz },
            ort = cols.getOrElse(6) { current.ort },
            land = cols.getOrElse(7) { current.land }.ifBlank { "DE" },
            istnetzwerk = cols.getOrElse(8) { checkboxText(current.istnetzwerk) } == "\u2705",
            istkunde = cols.getOrElse(9) { checkboxText(current.istkunde) } == "\u2705",
            lizenzkey = cols.getOrElse(11) { current.lizenzkey },
            nodePort = cols.getOrElse(12) { current.nodePort },
            rechnungsdatum = cols.getOrElse(13) { current.rechnungsdatum }.ifBlank { "1" },
            gesperrt = cols.getOrElse(15) { checkboxText(current.gesperrt) } == "\u2705",
            sperrgrund = cols.getOrElse(16) { current.sperrgrund.orEmpty() }.ifBlank { null }
        )
        return base.copy(zustand = deriveKundenZustand(base))
    }

    suspend fun saveSetupToLizenz(kunde: All) {
        val setupKey = kunde.firmenId
        if (setupKey.isBlank()) return

        val lizenzDetails = All(
            id = setupKey,
            firmenId = setupKey,
            lizenzkey = kunde.lizenzkey,
            nodePort = kunde.nodePort,
            rechnungsdatum = kunde.rechnungsdatum.ifBlank { "1" },
            nodeEingerichtet = nodeEingerichtet(kunde)
        )
        KundenSetupApi.saveDetails(setupKey, lizenzDetails)
    }

    fun resolveKunde(id: String): All? =
        draftKunden[id] ?: kunden.firstOrNull { it.id == id }

    fun applyDraft(id: String, column: String, value: String) {
        val current = resolveKunde(id) ?: return
        val updated = when (column) {
            "Firmen-ID" -> current.copy(firmenId = value)
            "Firma" -> current.copy(firma = value)
            "Straße" -> current.copy(strasse = value)
            "Hausnummer" -> current.copy(hausnummer = value)
            "PLZ" -> current.copy(plz = value)
            "Ort" -> current.copy(ort = value)
            "Land" -> current.copy(land = value)
            "Lizenzkey" -> current.copy(lizenzkey = value)
            "Nodeport" -> current.copy(nodePort = value)
            "Abrechnungstag" -> current.copy(rechnungsdatum = value)
            "Zugang gesperrt" -> current.copy(gesperrt = value == "\u2705")
            "Sperrgrund" -> current.copy(sperrgrund = value.ifBlank { null })
            else -> return
        }.let { it.copy(zustand = deriveKundenZustand(it)) }

        draftKunden = draftKunden.toMutableMap().apply { this[id] = updated }
    }

    fun canInstallSelection(selected: Set<String>): Boolean =
        selected.isNotEmpty() && selected.all { id ->
            val kunde = resolveKunde(id) ?: return@all false
            !kunde.aktiv &&
                kunde.firmenId.isNotBlank() &&
                kunde.lizenzkey.isNotBlank() &&
                kunde.nodePort.isNotBlank()
        }

    suspend fun persistRegisterRows(rows: List<Pair<String, List<String>>>) {
        val existingById = kunden.associateBy { it.id }
        val updated = mutableListOf<All>()

        rows.forEach { (rowId, cols) ->
            val provisional = rowId.ifBlank { cols.getOrElse(1) { "" } }
            val existing = existingById[provisional]
            val candidate = buildKunde(provisional, cols, existing)
            val hasContent = candidate.firma.isNotBlank() || candidate.strasse.isNotBlank() || candidate.plz.isNotBlank()
            if (!hasContent) return@forEach

            val registerEintrag = candidate.copy(
                id = candidate.id.ifBlank { candidate.firmenId },
                firmenId = candidate.firmenId,
                istnetzwerk = existing?.istnetzwerk ?: candidate.istnetzwerk,
                istkunde = existing?.istkunde ?: candidate.istkunde
            ).let { it.copy(zustand = deriveKundenZustand(it)) }

            val savedRegisterEintrag = KundenSetupApi.saveCompany(registerEintrag) ?: return@forEach
            if (savedRegisterEintrag.lizenzkey.isNotBlank() || savedRegisterEintrag.nodePort.isNotBlank() || savedRegisterEintrag.gesperrt == true || !savedRegisterEintrag.sperrgrund.isNullOrBlank()) {
                saveSetupToLizenz(savedRegisterEintrag)
            }
            updated += savedRegisterEintrag
        }

        if (updated.isNotEmpty()) {
            val byId = updated.associateBy { it.id }
            kunden = kunden.filterNot { current -> byId.containsKey(current.id) } + updated
            draftKunden = draftKunden - byId.keys
        }
    }

    LaunchedEffect(art, kundenLiveVersion) {
        kunden = letzteFilter?.let { filters ->
            runCatching { KundenSetupApi.searchSetup(filters).sortedBy { it.firma.lowercase() } }
                .getOrDefault(emptyList())
        }.orEmpty()
        draftKunden = emptyMap()
    }

    TabularScreen(
        screenKey = "KunenSetup",
        rollenKey = "KunenSetup",
        title = "Kunden Setup",
        headers = listOf(
            "ID",
            "Firmen-ID",
            "Firma",
            "Straße",
            "Hausnummer",
            "PLZ",
            "Ort",
            "Land",
            "Netzwerk",
            "Kunde",
            "Zustand",
            "Lizenzkey",
            "Nodeport",
            "Abrechnungstag",
            "Node eingerichtet",
            "Zugang gesperrt",
            "Sperrgrund"
        ),
        models = kunden,
        data = kunden.map { it.id to mapRow(it) },
        editable = true,
        includeMetadata = true,
        allowCreate = false,
        allowSave = true,
        allowDelete = false,
        nonEditableCols = setOf(
            "ID",
            "Netzwerk",
            "Kunde",
            "Zustand",
            "Node eingerichtet"
        ),
        dropdownOptions = mapOf(
            "Zugang gesperrt" to listOf("\u2b1c", "\u2705"),
            "Sperrgrund" to listOf("", "Rueckstand", "Gekuendigt")
        ),
        createRow = {
            listOf(
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "DE",
                "",
                "",
                ZustandManager.Kunde.textFor(ZustandManager.Kunde.ANGELEGT),
                "",
                "",
                "1",
                "\u2b1c",
                "\u2b1c",
                ""
            )
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                kunden = KundenSetupApi.searchSetup(filters).sortedBy { it.firma.lowercase() }
                draftKunden = emptyMap()
            }
        },
        onCommit = { rows ->
            scope.launch { persistRegisterRows(rows) }
        },
        onInstallSelected = { selected ->
            scope.launch {
                val updatedByPreviousId = mutableMapOf<String, All>()

                selected.forEach { id ->
                    val kunde = resolveKunde(id) ?: return@forEach
                    val firmenId = kunde.firmenId.ifBlank { kunde.id }
                    val lizenzkey = kunde.lizenzkey
                    val nodePort = kunde.nodePort
                    if (firmenId.isBlank() || lizenzkey.isBlank() || nodePort.isBlank()) return@forEach

                    val setupOk = KundenSetupApi.ensureCustomerNodeSetup(firmenId, lizenzkey, nodePort)
                    if (!setupOk) return@forEach

                    val installed = kunde.copy(
                        id = firmenId,
                        firmenId = firmenId,
                        aktiv = true,
                        istkunde = true
                    ).let { it.copy(zustand = deriveKundenZustand(it)) }

                    KundenSetupApi.saveCompany(installed)
                    saveSetupToLizenz(installed)
                    KundenverbrauchService.appendOnboardingOnInstall(installed)
                    KundenverbrauchService.appendLizenzierungOnAktiv(installed)
                    updatedByPreviousId[id] = installed
                }

                if (updatedByPreviousId.isNotEmpty()) {
                    kunden = kunden.map { current -> updatedByPreviousId[current.id] ?: current }
                    draftKunden = draftKunden - updatedByPreviousId.keys
                }
            }
        },
        isInstallEnabled = ::canInstallSelection,
        onCellChanged = ::applyDraft
    )
}
